import { serviceGo } from '@/utils/request'

// 分书新增数据：每本书一行
export interface BookNewDataRow {
  book_id: number
  book_title: string
  impression_uv: number   // 列表页曝光人数
  detail_view_uv: number  // 列表页点击人数（进入详情页）
  enter_uv: number        // 进入剧情人数
  // 进入剧情用户中，玩到的最大章节序号 >= N 的人数
  ch1: number
  ch2: number
  ch3: number
  ch4: number
  ch5: number
  ch6: number
  ch7: number
  ch8: number
  ch9: number
  ch10: number
  ch11: number
  ch12: number
  ch13: number
  ch14: number
  ch15: number
  // 发消息分层：进入剧情用户中、在该书内发出的消息条数达阈值的人数。分母均为 enter_uv。
  // 口径与「小说数据」「广告数据」两页一致（只算 AI 剧情里用户亲自发的消息，多份存档副本只算一次）。
  msg1: number    // 在该书内发 ≥1 条
  msg3: number    // ≥3 条
  msg5: number    // ≥5 条
  msg10: number   // ≥10 条
  msg20: number   // ≥20 条
  msg30: number   // ≥30 条
  msg50: number   // ≥50 条
  msg100: number  // ≥100 条
  // 付费（能量消费）分层：进入剧情用户中、在该书内累计消费果肉（能量）达阈值的人数。分母均为 enter_uv。
  pay0: number    // 消费 > 0（付费率分子）
  pay10: number   // ≥10 果肉
  pay20: number   // ≥20
  pay30: number   // ≥30
  pay50: number   // ≥50
  pay100: number  // ≥100
  pay300: number  // ≥300
}

export interface BookNewDataParams {
  user_scope: 'today' | 'last3' | 'last7' | 'all'  // 用户范围（注册日期）
  start_date: string   // 行为日期范围起（yyyy-MM-dd，北京时间，闭区间）
  end_date: string     // 行为日期范围止
  channel: 'toutiao' | 'kuaishou' | 'other' | 'all'
}

// 拉取分书新增数据（实时，debug/校验用；Go 后端，需管理员权限）
export async function getBookNewData(params: BookNewDataParams): Promise<BookNewDataRow[]> {
  const res = await serviceGo.get('/api_v2/admin/book-new-data', { params })
  return res.data.data || []
}

// ===== 每日预计算看板 =====

// 一个看板分组
export interface BookDailyBoard {
  board_key: string
  board_name: string
  reg_start: string   // 注册范围起 yyyy-MM-dd
  reg_end: string     // 注册范围止(不含,= run_date) yyyy-MM-dd
  n_days: number
  rows: BookNewDataRow[]
}

// 某 run_date 的全部看板
export interface BookDailyResp {
  run_date: string
  boards: BookDailyBoard[]
}

// 拉取有数据的可选日期(DESC)
export async function getBookDailyDates(): Promise<string[]> {
  const res = await serviceGo.get('/api_v2/admin/book-daily/dates')
  return res.data.data || []
}

// 拉取某 run_date 的预计算看板
export async function getBookDaily(runDate: string): Promise<BookDailyResp> {
  const res = await serviceGo.get('/api_v2/admin/book-daily', { params: { run_date: runDate } })
  return res.data.data || { run_date: runDate, boards: [] }
}

// 导出分书新增数据的某一个看板为 Excel。后端读的是同一份预计算数据，
// 不重算，所以导出的数字和页面上看到的必然相同。
// 返回整个 axios 响应而不是 res.data：文件名要从 Content-Disposition 头里取。
export function exportBookDaily(runDate: string, boardKey: string) {
  return serviceGo.get('/api_v2/admin/book-daily/export', {
    params: { run_date: runDate, board_key: boardKey },
    responseType: 'blob'
  })
}

// 发消息分层（实时补算，不在预计算表里）：页面主表渲染完后再异步取，不阻塞首屏。
// 首次约 6~7 秒（8 个看板并行），之后 1 小时内后端命中缓存秒回。
export interface BookMsgTiers {
  book_id: number
  tiers: number[]   // 顺序与 thresholds 一致
}

export interface BookDailyMsgResp {
  run_date: string
  thresholds: number[]                    // [1,3,5,10,20,30,50,100]
  boards: Record<string, BookMsgTiers[]>  // board_key → 该看板每本书的分层人数
}

export async function getBookDailyMsg(runDate: string): Promise<BookDailyMsgResp> {
  const res = await serviceGo.get('/api_v2/admin/book-daily/msg', { params: { run_date: runDate } })
  return res.data.data
}
